// Uyarlanmış kopya — emsal: beltasapp/frontend/src/shared/lib/use-kaydetmeden-cikis.tsx.
// Fark: uygulama `BrowserRouter` kullanır, `useBlocker` yoktur. Çıkış uygulama içi bağlantı
// tıklamasında (yakalama aşaması, `Link` işleyicisinden önce), sekme kapatma/yenilemede
// (`beforeunload`) ve ekranın kendi kapatma isteğinde (`confirm`: Sheet dışına tıklama, İptal)
// yakalanır. Tarayıcının geri/ileri düğmesi yakalanmaz. Diyalog iki yollu: Vazgeç · Kaydetmeden çık.
import { useEffect, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'

import { ConfirmDialog } from '@/components/feedback/ConfirmDialog'
import { messages } from '@/lib/messages'

export interface LeaveGuard {
  /** Kirliyse onay sorar, onaylanınca `action` çalışır; temizse hemen çalışır. */
  confirm: (action: () => void) => void
  /** "Kaydedilmemiş değişiklikler" onayı; ekranın JSX'ine konur. */
  dialog: ReactNode
}

/**
 * Kaydedilmemiş değişiklik varken çıkış koruması. `discard` onaylanınca kirli durumu temizler
 * (RHF `reset`); ardından bekleyen eylem (gezinme ya da kapatma) çalışır.
 */
export function useLeaveGuard(dirty: boolean, discard?: () => void): LeaveGuard {
  const navigate = useNavigate()
  const [pending, setPending] = useState<(() => void) | null>(null)

  useEffect(() => {
    if (!dirty) return
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      // Eski tarayıcılar için; metin gösterilmez.
      event.returnValue = ''
    }
    const onClick = (event: MouseEvent) => {
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)
        return
      const anchor = (event.target as Element | null)?.closest?.('a[href]')
      const href = anchor?.getAttribute('href')
      if (!href?.startsWith('/') || anchor?.getAttribute('target') === '_blank') return
      event.preventDefault()
      // Sayfada birden çok açık form varsa tek diyalog açılır.
      event.stopImmediatePropagation()
      setPending(() => () => navigate(href))
    }
    window.addEventListener('beforeunload', onBeforeUnload)
    document.addEventListener('click', onClick, true)
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload)
      document.removeEventListener('click', onClick, true)
    }
  }, [dirty, navigate])

  function confirm(action: () => void) {
    if (dirty) setPending(() => action)
    else action()
  }

  const dialog = (
    <ConfirmDialog
      open={pending !== null}
      onOpenChange={(open) => !open && setPending(null)}
      title={messages.form.unsavedTitle}
      description={messages.form.unsavedText}
      confirmLabel={messages.form.leaveWithoutSaving}
      onConfirm={() => {
        const action = pending
        setPending(null)
        discard?.()
        action?.()
      }}
    />
  )

  return { confirm, dialog }
}
